import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { Add as AddIcon, Visibility, Close, Delete as DeleteIcon } from '@mui/icons-material';
import { customerPaymentService } from '../services/customerPaymentService';
import { customerService } from '../services/customerService';
import { format } from 'date-fns';
import { formatCurrency } from '../components/CurrencyTextField';
import RecordPaymentDialog from '../components/RecordPaymentDialog';
import ResizableHeaderCell from '../components/ResizableHeaderCell';
import { useResizableColumns } from '../hooks/useResizableColumns';

const PAYMENT_METHODS = ['Check', 'ACH', 'Cash', 'Wire', 'Credit Card'];

const CustomerPayments = () => {
  const DEFAULT_COL_WIDTHS = {
    paymentNo: 130,
    customer: 200,
    order: 140,
    invoice: 168,
    date: 120,
    amount: 120,
    method: 140,
    reference: 160,
    actions: 116
  };
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [sortKey, setSortKey] = useState('payment_number');
  const [sortDirection, setSortDirection] = useState('desc');
  const { colWidths, startResize } = useResizableColumns({
    defaultWidths: DEFAULT_COL_WIDTHS,
    storageKey: 'wm_customer_payments_col_widths',
    minWidths: {
      paymentNo: 100,
      customer: 120,
      order: 100,
      invoice: 120,
      date: 100,
      amount: 100,
      method: 110,
      reference: 120,
      actions: 100
    }
  });

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const loadCustomers = async () => {
    try {
      const res = await customerService.getAll({ limit: 500 });
      setCustomers(res?.data ?? []);
    } catch {
      setCustomers([]);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');
      const params = { limit: 200 };
      if (customerFilter) params.customer_id = customerFilter;
      const res = await customerPaymentService.getAll(params);
      const list = res?.data ?? [];
      setPayments(list);
      setSelectedPayment(list.length > 0 ? list[0] : null);
    } catch (err) {
      console.error('Error loading payments:', err);
      setError(err.response?.data?.message || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    loadPayments();
  }, [customerFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MM/dd/yyyy');
    } catch {
      return dateString;
    }
  };

  const customerName = (p) =>
    p == null
      ? '—'
      : p.company_name || [p.customer_first_name, p.customer_last_name].filter(Boolean).join(' ') || '—';

  const orderDisplayId = (p) => {
    if (p == null) return null;
    return p.resolved_work_order_id != null && p.resolved_work_order_id !== ''
      ? p.resolved_work_order_id
      : p.work_order_id;
  };

  const orderLabel = (p) => {
    if (p == null) return null;
    return (
      p.resolved_work_order_label ||
      p.work_order_identifier ||
      p.work_order_number ||
      (orderDisplayId(p) != null ? `#${orderDisplayId(p)}` : null)
    );
  };

  const allocatedInvoicesList = (p) => {
    let v = p?.allocated_invoices;
    if (v == null) return [];
    if (typeof v === 'string') {
      try {
        v = JSON.parse(v);
      } catch {
        return [];
      }
    }
    return Array.isArray(v) ? v : [];
  };

  const handleRecordSuccess = () => {
    setRecordDialogOpen(false);
    loadPayments();
  };

  const handleConfirmListDelete = async () => {
    if (!deleteTarget?.id) return;
    const removedId = deleteTarget.id;
    try {
      setDeleteSubmitting(true);
      setError('');
      await customerPaymentService.delete(removedId);
      setDeleteTarget(null);
      if (selectedPayment?.id === removedId) setSelectedPayment(null);
      await loadPayments();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete payment');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const handleSort = (key) => {
    setSortDirection((prev) => (sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortKey(key);
  };

  const displayedPayments = useMemo(() => {
    const list = [...payments];
    const getter = (row) => {
      switch (sortKey) {
        case 'payment_number': return row.payment_number ?? '';
        case 'customer': return row.company_name || `${row.customer_first_name || ''} ${row.customer_last_name || ''}`.trim();
        case 'payment_date': return row.payment_date ? new Date(row.payment_date).getTime() : 0;
        case 'amount': return Number(row.amount) || 0;
        case 'payment_method': return row.payment_method ?? '';
        case 'reference_number': return row.reference_number ?? '';
        case 'work_order': {
          const lbl = orderLabel(row);
          return lbl != null && lbl !== '' ? String(lbl) : orderDisplayId(row) != null ? String(orderDisplayId(row)) : '';
        }
        case 'invoices': {
          const inv = allocatedInvoicesList(row);
          return inv.map((x) => x.label).join(', ');
        }
        default: return '';
      }
    };
    const dir = sortDirection === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      const va = getter(a);
      const vb = getter(b);
      if (typeof va === 'number' && typeof vb === 'number') return dir * (va - vb);
      return dir * String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    });
    return list;
  }, [payments, sortKey, sortDirection]);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
          Customer Payments
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setRecordDialogOpen(true)}
          >
            Record Payment
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Visibility />}
            onClick={() => selectedPayment && navigate(`/customer-payments/${selectedPayment.id}`)}
            disabled={!selectedPayment}
          >
            View
          </Button>
          {selectedPayment && (
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={() => setDeleteTarget(selectedPayment)}
            >
              Delete
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          {error}
        </Alert>
      )}

      {/* Selected Payment Information Bar */}
      <Paper sx={{ p: 1.5, mb: 1.5, backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Payment #</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{selectedPayment?.payment_number ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Customer</Typography>
            <Typography variant="body2">
              {selectedPayment?.customer_id ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/customers/${selectedPayment.customer_id}`)}
                >
                  {customerName(selectedPayment) || `#${selectedPayment.customer_id}`}
                </Button>
              ) : (
                selectedPayment ? customerName(selectedPayment) : '—'
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Order</Typography>
            <Typography variant="body2">
              {orderDisplayId(selectedPayment) != null ? (
                <Button
                  size="small"
                  variant="text"
                  sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                  onClick={() => navigate(`/work-orders/${orderDisplayId(selectedPayment)}`)}
                >
                  {orderLabel(selectedPayment) || `Order #${orderDisplayId(selectedPayment)}`}
                </Button>
              ) : (
                '—'
              )}
            </Typography>
          </Box>
          <Box sx={{ maxWidth: 280 }}>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Invoice</Typography>
            <Typography variant="body2" component="div">
              {allocatedInvoicesList(selectedPayment || {}).length === 0 ? (
                '—'
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                  {allocatedInvoicesList(selectedPayment).map((inv) => (
                    <Button
                      key={inv.id}
                      size="small"
                      variant="text"
                      sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                      onClick={() => navigate(`/customer-invoices/${inv.id}`)}
                    >
                      {inv.label}
                    </Button>
                  ))}
                </Box>
              )}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Date</Typography>
            <Typography variant="body2">{selectedPayment ? formatDate(selectedPayment.payment_date) : '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Amount</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {selectedPayment ? formatCurrency(selectedPayment.amount, '—') : '—'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Method</Typography>
            <Typography variant="body2">{selectedPayment?.payment_method ?? '—'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', display: 'block' }}>Reference</Typography>
            <Typography variant="body2">{selectedPayment?.reference_number ?? '—'}</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton size="small" onClick={() => setSelectedPayment(null)} disabled={!selectedPayment} sx={{ ml: 'auto' }} title="Clear selection">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Paper>

      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Customer</InputLabel>
            <Select
              value={customerFilter}
              onChange={(e) => setCustomerFilter(e.target.value)}
              label="Customer"
            >
              <MenuItem value="">All</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.company_name || [c.first_name, c.last_name].filter(Boolean).join(' ') || `#${c.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={loadPayments}>
            Refresh
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <ResizableHeaderCell width={colWidths.paymentNo} onResizeStart={(e) => startResize('paymentNo', e)} sortable sortActive={sortKey === 'payment_number'} sortDirection={sortDirection} onSort={() => handleSort('payment_number')}>Payment #</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.customer} onResizeStart={(e) => startResize('customer', e)} sortable sortActive={sortKey === 'customer'} sortDirection={sortDirection} onSort={() => handleSort('customer')}>Customer</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.order} onResizeStart={(e) => startResize('order', e)} sortable sortActive={sortKey === 'work_order'} sortDirection={sortDirection} onSort={() => handleSort('work_order')}>Order</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.invoice} onResizeStart={(e) => startResize('invoice', e)} sortable sortActive={sortKey === 'invoices'} sortDirection={sortDirection} onSort={() => handleSort('invoices')}>Invoice</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.date} onResizeStart={(e) => startResize('date', e)} sortable sortActive={sortKey === 'payment_date'} sortDirection={sortDirection} onSort={() => handleSort('payment_date')}>Date</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.amount} onResizeStart={(e) => startResize('amount', e)} align="right" sortable sortActive={sortKey === 'amount'} sortDirection={sortDirection} onSort={() => handleSort('amount')}>Amount</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.method} onResizeStart={(e) => startResize('method', e)} sortable sortActive={sortKey === 'payment_method'} sortDirection={sortDirection} onSort={() => handleSort('payment_method')}>Method</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.reference} onResizeStart={(e) => startResize('reference', e)} sortable sortActive={sortKey === 'reference_number'} sortDirection={sortDirection} onSort={() => handleSort('reference_number')}>Reference</ResizableHeaderCell>
                <ResizableHeaderCell width={colWidths.actions} onResizeStart={(e) => startResize('actions', e)} align="right">Actions</ResizableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    No payments found. Click &quot;Record Payment&quot; to add one.
                  </TableCell>
                </TableRow>
              ) : (
                displayedPayments.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: selectedPayment?.id === p.id ? 'action.selected' : 'inherit'
                    }}
                    onClick={() => setSelectedPayment(p)}
                    onDoubleClick={() => navigate(`/customer-payments/${p.id}`)}
                  >
                    <TableCell sx={{ width: colWidths.paymentNo, fontWeight: 500 }}>{p.payment_number}</TableCell>
                    <TableCell sx={{ width: colWidths.customer }}>{customerName(p)}</TableCell>
                    <TableCell sx={{ width: colWidths.order }}>
                      {orderDisplayId(p) != null ? (
                        <Button
                          size="small"
                          variant="text"
                          sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/work-orders/${orderDisplayId(p)}`);
                          }}
                        >
                          {orderLabel(p) || `Order #${orderDisplayId(p)}`}
                        </Button>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.invoice, verticalAlign: 'top' }}>
                      {allocatedInvoicesList(p).length === 0 ? (
                        '—'
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                          {allocatedInvoicesList(p).map((inv) => (
                            <Button
                              key={inv.id}
                              size="small"
                              variant="text"
                              sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/customer-invoices/${inv.id}`);
                              }}
                            >
                              {inv.label}
                            </Button>
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell sx={{ width: colWidths.date }}>{formatDate(p.payment_date)}</TableCell>
                    <TableCell sx={{ width: colWidths.amount }} align="right">{formatCurrency(p.amount, '—')}</TableCell>
                    <TableCell sx={{ width: colWidths.method }}>{p.payment_method || '—'}</TableCell>
                    <TableCell sx={{ width: colWidths.reference }}>{p.reference_number || '—'}</TableCell>
                    <TableCell sx={{ width: colWidths.actions }} align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/customer-payments/${p.id}`)}
                        title="View"
                      >
                        <Visibility fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Delete payment"
                        onClick={() => setDeleteTarget(p)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleteSubmitting && setDeleteTarget(null)}>
        <DialogTitle>Delete this payment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {deleteTarget
              ? `${deleteTarget.payment_number} (${formatCurrency(deleteTarget.amount, '—')}) will be removed.`
              : ''}
            {deleteTarget && allocatedInvoicesList(deleteTarget).length > 0
              ? ' Invoice allocations will be reversed.'
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmListDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <RecordPaymentDialog
        open={recordDialogOpen}
        onClose={() => setRecordDialogOpen(false)}
        onSuccess={handleRecordSuccess}
      />
    </Box>
  );
};

export default CustomerPayments;
